import React, { memo, useState } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Icons from "../../../assets/Icons";
import { COPILOT_BROADCAST, COPILOT_RECIPIENT } from "../types/CommonTypes";
import RecipientPicker from "./RecipientPicker";

const BROADCAST_ACCENT = "#312C85";
const BROADCAST_BG = "#EEF2FF";
const BROADCAST_BORDER = "#C6D2FF";

const BroadcastIcon = () => (
  <Icons.BroadcastIcon width={16} height={16} style={{ flexShrink: 0, color: BROADCAST_ACCENT }} />
);

interface BroadcastCardProps {
  messageId: string;
  broadcast: COPILOT_BROADCAST;
  onSend?: (messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => void;
  onDismiss?: (messageId: string) => void;
  onRetry?: (messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => void;
}

const BroadcastCard = memo(function BroadcastCard({
  messageId,
  broadcast,
  onSend,
  onDismiss,
  onRetry,
}: BroadcastCardProps) {
  const { t } = useTranslation();
  const {
    status,
    initialRecipients,
    recipientOptions,
    initialMessage,
    executingCount,
    successCount,
    successSentAt,
    successRecipients,
  } = broadcast;

  const [selectedRecipients, setSelectedRecipients] = useState<COPILOT_RECIPIENT[]>(
    initialRecipients ?? []
  );
  const [messageText, setMessageText] = useState(initialMessage ?? "");
  const toggleRecipient = (opt: COPILOT_RECIPIENT) => {
    setSelectedRecipients((prev) =>
      prev.some((r) => r.id === opt.id) ? prev.filter((r) => r.id !== opt.id) : [...prev, opt]
    );
  };

  const removeRecipient = (id: string) => {
    setSelectedRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  const canSend = selectedRecipients.length > 0 && messageText.trim().length > 0;

  // Dismissed: render nothing
  if (status === "dismissed") return null;

  return (
    <Box
      sx={{
        mt: 1,
        width: "85%",
        borderRadius: "12px",
        bgcolor: BROADCAST_BG,
        border: `1px solid ${BROADCAST_BORDER}`,
        p: 2,
        boxSizing: "border-box",
      }}
    >
      {/* ── Header ── */}
      <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
        <BroadcastIcon />
        <Typography
          sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: BROADCAST_ACCENT, lineHeight: "20px" }}
        >
          {t("Copilot_Broadcast_Heading")}
        </Typography>
        {!status && (
          <Box
            component="button"
            onClick={() => onDismiss?.(messageId)}
            sx={{
              display: "flex",
              alignItems: "center",
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              color: "#6B7280",
              "&:hover": { color: "#374151" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>

      {/* ── Idle — form ── */}
      {!status && (
        <>
          <RecipientPicker
            selectedRecipients={selectedRecipients}
            recipientOptions={recipientOptions}
            onToggle={toggleRecipient}
            onRemove={removeRecipient}
          />

          {/* MESSAGE */}
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "text.primary", mb: 0.75 }}
          >
            {t("Copilot_Recommend_Message_Label")}
          </Typography>

          <Box
            component="textarea"
            value={messageText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessageText(e.target.value)}
            placeholder={t("Copilot_Broadcast_Textarea_Placeholder")}
            rows={4}
            sx={{
              display: "block",
              width: "100%",
              resize: "none",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              px: 1.25,
              py: 1,
              fontSize: 13,
              lineHeight: "20px",
              fontFamily: "inherit",
              color: "inherit",
              bgcolor: "#FFFFFF",
              mb: 1.5,
              boxSizing: "border-box",
              "&::placeholder": { color: "#9CA3AF" },
              "&:focus": { outline: "none", border: `1px solid ${BROADCAST_ACCENT}` },
            }}
          />

          {/* Buttons */}
          <Box display="flex" gap={1.25}>
            <Box
              component="button"
              onClick={() => canSend && onSend?.(messageId, selectedRecipients, messageText)}
              disabled={!canSend}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#5921F2",
                opacity: canSend ? 1 : 0.5,
                color: "#FFFFFF",
                border: "none",
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: canSend ? "pointer" : "default",
                "&:hover": canSend ? { opacity: 0.9 } : { opacity: 0.5 },
                "&:active": canSend ? { opacity: 0.8 } : { opacity: 0.5 },
              }}
            >
              <Icons.SendMessageWhite width={16} height={16} style={{ flexShrink: 0 }} />
              {t("Copilot_Broadcast_Send")}
            </Box>
            <Box
              component="button"
              onClick={() => onDismiss?.(messageId)}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#FFFFFF",
                color: "text.primary",
                border: "1px solid #D8C4FF",
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Cancel")}
            </Box>
          </Box>
        </>
      )}

      {/* ── Executing ── */}
      {status === "executing" && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} sx={{ color: "#155DFC", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, color: "#1447E6", lineHeight: "20px" }}>
            {t("Copilot_Broadcast_Sending", { count: executingCount ?? 0 })}
          </Typography>
        </Box>
      )}

      {/* ── Success ── */}
      {status === "success" && (
        <Box
          sx={{
            borderRadius: "12px",
            bgcolor: "#F0FDF4",
            border: "1px solid #B9F8CF",
            px: 1.75,
            py: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Icons.AlertSuccess width={18} height={18} style={{ flexShrink: 0 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0D542B", lineHeight: "22px" }}>
              {t("Copilot_Broadcast_Success_Title")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "#007A02", lineHeight: "19px", mb: 1 }}>
            {t("Copilot_Broadcast_Success_Sub", {
              count: successCount ?? 0,
              time: successSentAt ?? "",
            })}
          </Typography>
          <Divider sx={{ mb: 1, borderColor: "#BBF7D0" }} />
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "#0D542B", mb: 0.75 }}
          >
            {t("Copilot_Broadcast_Recipients_Label")}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {(successRecipients ?? []).map((label) => (
              <Box
                key={label}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  px: 1.25,
                  py: 0.375,
                  bgcolor: "#DCFCE7",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#166534", lineHeight: "20px" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Error ── */}
      {status === "error" && (
        <>
          <Box
            sx={{
              borderRadius: "12px",
              bgcolor: "#F8E5E9",
              border: "1px solid #FFC9C9",
              px: 1.75,
              py: 1.5,
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#E00000", lineHeight: "24px", mb: 0.5 }}>
              {t("Copilot_Recommend_Error_Heading")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#E00000", lineHeight: "20px" }}>
              {t("Copilot_Recommend_Error_Body")}
            </Typography>
          </Box>
          <Box display="flex" gap={1.25}>
            <Box
              component="button"
              onClick={() =>
                onRetry?.(messageId, selectedRecipients, messageText)
              }
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#5921F2",
                color: "#FFFFFF",
                border: "none",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Retry")}
            </Box>
            <Box
              component="button"
              onClick={() => onDismiss?.(messageId)}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#FFFFFF",
                color: "text.primary",
                border: "1px solid #D8C4FF",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Dismiss")}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

export default BroadcastCard;
